import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Split variant styles using Class Variance Authority (CVA)
 *
 * Defines gap, breakpoint, ratio, and reverse options for responsive two-column layouts.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Gap Options
 * - xxs through 3xl matching design tokens
 *
 * ## Breakpoint (when to switch from column to row)
 * - sm: 640px, md: 768px, lg: 1024px, xl: 1280px
 *
 * ## Ratio (column width ratios)
 * - equal: 50/50
 * - 1/3-2/3: First column 1/3, second 2/3
 * - 2/3-1/3: First column 2/3, second 1/3
 * - 1/4-3/4: First column 1/4, second 3/4
 * - 3/4-1/4: First column 3/4, second 1/4
 */
export const splitVariants = cva('flex flex-col items-stretch', {
  variants: {
    gap: {
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
      '2xl': 'gap-2xl',
      '3xl': 'gap-3xl',
    },
    breakpoint: {
      sm: 'sm:flex-row',
      md: 'md:flex-row',
      lg: 'lg:flex-row',
      xl: 'xl:flex-row',
    },
    reverse: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { breakpoint: 'sm', reverse: true, class: 'sm:flex-row-reverse' },
    { breakpoint: 'md', reverse: true, class: 'md:flex-row-reverse' },
    { breakpoint: 'lg', reverse: true, class: 'lg:flex-row-reverse' },
    { breakpoint: 'xl', reverse: true, class: 'xl:flex-row-reverse' },
  ],
  defaultVariants: {
    gap: 'xl',
    breakpoint: 'md',
    reverse: false,
  },
});

export const splitItemVariants = cva('min-w-0 h-full', {
  variants: {
    ratio: {
      equal: 'basis-1/2 grow shrink',
      '1/3-2/3-first': 'basis-1/3',
      '1/3-2/3-second': 'basis-2/3',
      '2/3-1/3-first': 'basis-2/3',
      '2/3-1/3-second': 'basis-1/3',
      '1/4-3/4-first': 'basis-1/4',
      '1/4-3/4-second': 'basis-3/4',
      '3/4-1/4-first': 'basis-3/4',
      '3/4-1/4-second': 'basis-1/4',
    },
  },
  defaultVariants: {
    ratio: 'equal',
  },
});

export type SplitVariantProps = VariantProps<typeof splitVariants>;
export type SplitItemVariantProps = VariantProps<typeof splitItemVariants>;
